import { capitalizeWords, type RoomMeasurementKey } from "@rebuild/shared";
import type { MetricProps } from "./Metric";
import { Metric } from "./Metric";
import { Input } from "@/components/ui/input";
import { useCallback, useState } from "react";
import type { EstimateDetail } from "@/interfaces/estimate-detail";
import { useEstimateDetail } from "@/hooks/useEstimateDetail";
import { Archive, Clipboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { estimationActions } from "@/redux/actions/estimation.actions";
import { dispatch } from "@/redux/store";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useArchiveRoomMutation } from "@/hooks/mutation/estimates/useArchiveRoomMutation";
import { isItemArchived } from "@rebuild/shared";
import { useAnalytics } from "@/analytics";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";

interface RoomHeaderProps {
  estimateId: string;
  room: EstimateDetail;
  metrics: Array<MetricProps>;
  onUpdateMeasurement: (category: RoomMeasurementKey, value: string) => void;
  onUpdateRoomName: (newRoomName: string) => void;
  copiedItemCount?: number;
  onPaste?: () => void;
  isPasting?: boolean;
}

export const RoomHeader = ({
  estimateId,
  room,
  metrics,
  onUpdateMeasurement,
  onUpdateRoomName,
  copiedItemCount,
  onPaste,
  isPasting,
}: RoomHeaderProps) => {
  const { isFeatureEnabled } = useFeatureFlags();
  const isMetricsEnabled = isFeatureEnabled("WEBAPP_ROOM_DIMENSIONS_ENABLED");

  const { roomTabOptions, searchEstimateDetail } = useEstimateDetail(estimateId!);
  const { mutate: archiveRoom, isPending: isArchiving } = useArchiveRoomMutation();
  const { trackEvent, ANALYTICS_EVENTS } = useAnalytics();

  const [roomName, setRoomName] = useState(room.dataForUI.roomName);
  // The input keeps its own copy of the name; reset it when the room is renamed elsewhere
  // (the agent, mobile, another user), since useState only reads its initial value once.
  const [prevStoredRoomName, setPrevStoredRoomName] = useState(room.dataForUI.roomName);
  if (prevStoredRoomName !== room.dataForUI.roomName) {
    setPrevStoredRoomName(room.dataForUI.roomName);
    setRoomName(room.dataForUI.roomName);
  }
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [isErrorFading, setIsErrorFading] = useState(false);

  const pageId = room.dataForUI.metadata.pageId;

  const onRoomNameBlurred = useCallback(() => {
    const formatted = capitalizeWords(roomName);
    const isDuplicate = roomTabOptions.some((option) => option.value.toLowerCase() === formatted.toLowerCase() && option.id !== pageId);
    if (isDuplicate) {
      setNameError("A room with this name already exists.");
      setIsErrorFading(false);
      setTimeout(() => setIsErrorFading(true), 2_000);
      setTimeout(() => {
        setNameError(null);
        setIsErrorFading(false);
      }, 2_200);
      setRoomName(room.dataForUI.roomName);
      return;
    }
    setRoomName(formatted);
    onUpdateRoomName(formatted);
  }, [roomName, roomTabOptions, pageId, room.dataForUI.roomName, onUpdateRoomName]);

  const hasActiveLineItems = room.dataForUI.page.tuplesOutput.some(
    (tuple) => !isItemArchived(tuple) && tuple.lineItems.some((li) => !isItemArchived(li)),
  );

  const isGeneralRoom = !!room.dataForUI.metadata.roomName.match(/general/i);

  const onClick = useCallback(async () => {
    const currentRoom = roomTabOptions.find((option) => option.id === room.dataForUI.metadata.pageId);

    dispatch(estimationActions.setEstimationState({ currentRoom }));
  }, [room.dataForUI.metadata.pageId, roomTabOptions]);

  const handleArchive = () => {
    setShowArchiveConfirm(false);
    archiveRoom(
      { estimateId, pageId },
      {
        onSuccess: () => {
          trackEvent(ANALYTICS_EVENTS.ROOM_ARCHIVED, {
            estimate_id: estimateId,
            room_name: room.dataForUI.roomName,
            has_active_line_items: hasActiveLineItems,
          });
          searchEstimateDetail(estimateId, { silent: true });
        },
      },
    );
  };

  return (
    <>
      <div className="flex items-center justify-between border-b border-[#EEEFF1] bg-[#f5f5f5] p-2" id="header" onClick={onClick}>
        <div className="flex min-w-0 flex-1 items-center justify-start gap-2 p-2">
          <Input
            value={roomName}
            className="w-auto min-w-[6rem] max-w-full cursor-text border-transparent px-1 shadow-none [field-sizing:content] hover:border-input focus-visible:border-input focus-visible:bg-white"
            onChange={(e) => setRoomName(e.target.value)}
            onBlur={onRoomNameBlurred}
          />
          {nameError && (
            <span
              className={`shrink-0 text-sm text-red-500 transition-opacity duration-500 ${isErrorFading ? "opacity-0" : "opacity-100"}`}
            >
              {nameError}
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {copiedItemCount !== undefined && copiedItemCount > 0 && onPaste && (
            <Button
              variant="outline"
              size="sm"
              disabled={isPasting}
              onClick={(e) => {
                e.stopPropagation();
                onPaste();
              }}
            >
              <Clipboard className="mr-1 h-4 w-4" />
              {isPasting ? "Pasting..." : `Paste (${copiedItemCount})`}
            </Button>
          )}
        </div>
        {isMetricsEnabled && !isGeneralRoom && (
          <div className="flex shrink-0 items-center gap-3">
            {metrics.map((metric, index) => (
              <Metric
                key={index}
                type={metric.type}
                icon={metric.icon}
                value={metric.value}
                label={metric.label}
                category={metric.category}
                onUpdateMeasurement={onUpdateMeasurement}
              />
            ))}
          </div>
        )}
        <div className="flex shrink-0 items-center pl-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="destructive"
                  disabled={isArchiving}
                  className="h-7 w-7 shrink-0 rounded-md p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    trackEvent(ANALYTICS_EVENTS.ROOM_ARCHIVE_INITIATED, {
                      estimate_id: estimateId,
                      room_name: room.dataForUI.roomName,
                      has_active_line_items: hasActiveLineItems,
                    });
                    setShowArchiveConfirm(true);
                  }}
                >
                  <Archive className="h-4 w-4 shrink-0" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="bg-background text-foreground">Archive Room</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      <AlertDialog open={showArchiveConfirm} onOpenChange={setShowArchiveConfirm}>
        <AlertDialogContent className="bg-background text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive "{room.dataForUI.roomName}"?</AlertDialogTitle>
            <AlertDialogDescription>
              {hasActiveLineItems
                ? "This room contains line items. Archiving will also archive all line items in this room. This action cannot be undone from the webapp."
                : "This room will be archived and removed from the estimate view. This action cannot be undone from the webapp."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() =>
                trackEvent(ANALYTICS_EVENTS.ROOM_ARCHIVE_CANCELLED, {
                  estimate_id: estimateId,
                  room_name: room.dataForUI.roomName,
                })
              }
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleArchive}>
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
